import React, { ReactNode, useCallback } from "react";

import cn from "classnames";
import { useIntl } from "react-intl";

import { LoadingSpinner } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import IMapViewContext from "@app/component/view/types/IMapViewContext";
import withMapView from "@app/scene/map/utils/withMapView";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";

import styles from "./Header.module.scss";

interface Props extends IMapViewContext {
  className?: string;
  count?: number;
  countClassName?: string;
  extra?: ReactNode;
}

const Header: React.FC<Props> = ({
  className,
  count,
  countClassName,
  extra,
  updateViewStateAndFetch,
  viewState,
}) => {
  const intl = useIntl();

  const noEntitiesVisible = viewState.visibleEntities.length === 0;

  const onEnablePins = useCallback(() => {
    updateViewStateAndFetch?.({ request: { visibleEntities: MAP_ENTITY_TYPES } });
  }, [updateViewStateAndFetch]);

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.count}>
        <span className={cn(styles.countLabel, countClassName)}>
          {noEntitiesVisible
            ? intl.formatMessage({
                id: "map.territories.sidebar.header.pinCount.noPinsVisible",
                defaultMessage: "No records visible",
                description:
                  "A warning to show when no pins are visible during creating/editing a territory",
              })
            : intl.formatMessage(
                {
                  id: "map.territories.sidebar.header.pinCount",
                  defaultMessage:
                    "{showLoader, select, true {{loader}} other {{count}}} {count, plural, one {record} other {records}} in bounds",
                  description: "Number of records in bounds in Territory Drawers",
                },
                {
                  count: count ?? 0,
                  loader: <LoadingSpinner mini />,
                  showLoader: count === undefined,
                }
              )}
        </span>
        {noEntitiesVisible && (
          <span className={styles.invisiblePinsWarning}>
            {intl.formatMessage({
              id: "map.territories.sidebar.header.pinCount.noPinsVisible.description",
              defaultMessage:
                "Please note, records will still be included in territory even if not pictured currently.",
              description:
                "A description to show when no pins are visible during creating/editing a territory",
            })}
            <br />
            <ButtonLink className={styles.invisiblePinsButton} onClick={onEnablePins}>
              {intl.formatMessage({
                id: "map.territories.sidebar.header.pinCount.noPinsVisible.action",
                defaultMessage: "Enable Pins",
                description:
                  "An action to show pins when all of them are currently hidden during creating/editing a territory",
              })}
            </ButtonLink>
          </span>
        )}
      </div>
      {extra}
    </div>
  );
};

export default withMapView(Header, ["visibleEntities"]);
